/**
 * useConsentModal
 *
 * Owns the two consent-form methods that were previously class methods on
 * FormViewer:
 *
 *   triggerUpdateForm()          — fires ReturnCalc to refresh field focus
 *                                  after the consent form closes
 *   hideConsentModal(isDisagreeing)   — clears the consent form state; blurs
 *                                  (agree) or resets TEXT00 (disagree)
 *
 * Returns:
 *   { triggerUpdateForm, hideConsentModal }
 */
import { useCallback } from 'react';
import { CALC_TYPE } from '~/app/constants';

const assetFormRegExp = /^[A]{1}[0-9]{5}/;

const useConsentModal = ({
  calcAPIRef,
  // Kept-current refs
  formNavRef, // kept-current ref to useFormNavigation result
  currentElRef, // shared ref to the currently focused field element
  fieldEventsRef, // kept-current ref to useFormFieldEvents result
}) => {
  /**
   * Fires ReturnCalc on the current field so the form re-evaluates focus
   * after the consent modal is dismissed.
   */
  const triggerUpdateForm = useCallback(() => {
    const formNav = formNavRef.current;
    calcAPIRef.current.ReturnCalc(
      formNav.activeForm,
      assetFormRegExp.test(formNav.activeForm) ? 'X' : '',
      currentElRef?.current?.id,
      currentElRef?.current?.value,
      CALC_TYPE.FIELD_CALC,
    );
  }, [calcAPIRef, currentElRef, formNavRef]);

  /**
   * Closes the consent form. On disagree, clears the TEXT00 field; on agree,
   * blurs the current element. Always re-triggers the update calc.
   *
   * @param {boolean} [isDisagreeing=false]
   */
  const hideConsentModal = useCallback(
    (isDisagreeing = false) => {
      const fieldEvents = fieldEventsRef.current;
      if (fieldEvents.showConsentForm) {
        if (isDisagreeing) {
          document.getElementById('TEXT00').value = '';
        } else {
          currentElRef?.current?.blur();
        }
        fieldEvents.setShowConsentForm(false);
        // Re-trigger update form so field focus is restored after consent close
        triggerUpdateForm();
      }
    },
    [currentElRef, fieldEventsRef, triggerUpdateForm],
  );

  return {
    triggerUpdateForm,
    hideConsentModal,
  };
};

export default useConsentModal;
